import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import {
  BookOpen,
  FileText,
  Edit,
  RefreshCw,
  HelpCircle
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';

import MyArticles from './knowledge/MyArticles';
import Drafts from './knowledge/Drafts';
import UserGuide from '../../components/UserGuide';

const resolveTabFromPath = (pathname) => {
  if (pathname.endsWith('/drafts')) return 'drafts';
  return null;
};

const UnifiedAgentKnowledgeBase = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const pathTab = resolveTabFromPath(location.pathname);
  const initialTab = pathTab || searchParams.get('tab') || 'user-guide';
  const [activeTab, setActiveTab] = useState(initialTab);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    if (tabId === 'drafts') {
      navigate('/agent/knowledge/drafts');
      return;
    }
    if (tabId === 'my-articles') {
      navigate('/agent/knowledge/my-articles');
      return;
    }
    if (tabId === 'user-guide') {
      navigate('/agent/knowledge/user-guide');
      return;
    }
    setSearchParams({ tab: tabId });
  };

  useEffect(() => {
    const tabFromPath = resolveTabFromPath(location.pathname);
    const tabFromUrl = tabFromPath || searchParams.get('tab') || 'user-guide';
    if (tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams, location.pathname, activeTab]);

  const tabs = [
    {
      id: 'my-articles',
      label: 'My Articles',
      icon: FileText,
      description: 'Articles created by you',
    },
    {
      id: 'drafts',
      label: 'Drafts',
      icon: Edit,
      description: 'Draft articles in progress',
    },
    {
      id: 'user-guide',
      label: 'User Guide',
      icon: HelpCircle,
      description: 'User guide management',
    },
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <BookOpen className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Knowledge Base</h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Manage and organize knowledge base articles
              </p>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/agent/dashboard')}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex space-x-1 overflow-x-auto px-4" aria-label="Knowledge Base Tabs">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => handleTabChange(tab.id)}
                    className={`flex items-center space-x-2 py-4 px-4 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:border-gray-300'
                    }`}
                    title={tab.description}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'my-articles' && (
              <div>
                <MyArticles />
              </div>
            )}

            {activeTab === 'drafts' && (
              <div>
                <Drafts />
              </div>
            )}

            {activeTab === 'user-guide' && (
              <div>
                <UserGuide />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default UnifiedAgentKnowledgeBase;
